import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    API_GATEWAY_PATH,
    AppGtmEcommerceOptions,
    CheckoutCreatePurchaseOrderRequest,
    CheckoutGetPurchaseOrderResponse,
    CheckoutUpdateOwnershipRequest,
    Cms,
    EcommerceProduct,
    GoToPaymentRequest,
    GoToPaymentResponse,
    IAtermarketEquipment,
    IAtermarketEquipmentTotalOrderPriceResponse,
    ILeasingUsedCarUpdateRequest,
    ISetAtermarketEquipmentRequest,
    ISetLoanRequestBody,
    ISetLoanType,
    LoanRequest,
    PostExchangeCarRequest,
    RequestsUpdateCashRequestDto,
    RequestsUpdateInsuranceRequestDto,
    RequestsUpdatePickupDeliveryRequestDto,
    ResponsesUpdateCompletedDto,
    TempAny,
    ValidatePRResponse,
} from '@impact/data';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, forkJoin, Observable, of, ReplaySubject, Subscription, throwError } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { ConsentService } from '../core/consent.service';
import { objectIsEmpty } from '../core/helpers';
import { SettingsService } from '../core/settings.service';
import { STORAGE_SERVICE_KEYS, StorageService } from '../core/storage.service';
import { ToastService } from '../core/toast.service';
import { IExchangeCar } from '../exchange-car/exchange-car.models';
import { LoanService } from '../loan/loan.service';
import { UsedCarFinancingTypes } from '../used-car-details/used-car.models';
import { mapProductToEcommerceVehicle } from '../utils/tracking/tracking-product.mapper';
import { TrackingService } from '../utils/tracking/tracking.service';
import { CHECKOUT_STEP, CheckoutFlowService } from './checkout-flow.service';

export interface IStoredActivePurchaseOrder extends CheckoutCreatePurchaseOrderRequest {
    purchaseOrderId?: string;
    currentCheckoutStep?: CHECKOUT_STEP;
}

export const UsedCarCheckoutFinancingTabsOrder = [
    UsedCarFinancingTypes.Cash,
    UsedCarFinancingTypes.Loan,
    UsedCarFinancingTypes.Leasing
];

@Injectable({
    providedIn: 'root',
})
export class CheckoutService {
    // eslint-disable-next-line rxjs/no-explicit-generics
    private purchaseOrderId$ = new BehaviorSubject<string | undefined>(undefined);
    private purchaseOrderIdSubscription: Subscription;
    private checkoutData$ = new ReplaySubject<CheckoutGetPurchaseOrderResponse>(1);
    public isCheckoutLoading$ = new ReplaySubject<boolean>(1);
    public checkoutFinalized$ = new BehaviorSubject(false);
    public showGenericCheckoutError$ = new BehaviorSubject(false);
    // eslint-disable-next-line rxjs/no-explicit-generics
    public purchaseOrderValidation$ = new BehaviorSubject<ValidatePRResponse | undefined>(undefined);
    public purchaseOrderValidationLoading$ = new BehaviorSubject(false);
    // eslint-disable-next-line rxjs/no-explicit-generics
    public aftermarketEquipmentData$ = new BehaviorSubject<IAtermarketEquipmentTotalOrderPriceResponse | undefined>(undefined);
    public aftermarketEquipmentLoading$ = new BehaviorSubject(false);
    public aftermarketEquipmentLoanLoading$ = new BehaviorSubject(false);
    public isBasketOnBehalf$ = new BehaviorSubject(false);
    private globalSettings: Cms.SettingsDto;
    private errorMessage_generic = 'Der opstod en fejl, prøv igen eller kontakt kundeservice';
    private errorMessage_couldNotGoToPayment = 'Der skete en fejl. Vi kunne ikke sende dig videre til betaling. Prøv igen, eller kontakt os.';

    // LEASING
    public checkoutFlowLeasingData$ = new BehaviorSubject({});
    checkoutFlowLeasingData: any;
    leasingConfigDataAndRanges$ = new BehaviorSubject<any>({});

    constructor(
        private readonly http: HttpClient,
        private readonly storageService: StorageService,
        private readonly checkoutFlowService: CheckoutFlowService,
        private readonly settingsService: SettingsService,
        private readonly toastService: ToastService,
        private readonly translateService: TranslateService,
        private readonly consentService: ConsentService,
        private readonly loanService: LoanService,
        private readonly trackingService: TrackingService
    ) {
        this.translateService
            .get(['checkout.error_message____generic', 'checkout.error_message____could_not_go_to_payment'])
            .pipe(take(1))
            .subscribe((translations: { [key: string]: string }) => {
                this.errorMessage_generic = translations['checkout.error_message____generic'];
                this.errorMessage_couldNotGoToPayment = translations['checkout.error_message____could_not_go_to_payment'];
            });

        this.settingsService.get().subscribe((settings) => (this.globalSettings = settings));
    }

    initCheckout(checkoutData: CheckoutCreatePurchaseOrderRequest, basketOnBehalfPurchaseOrderId?: string, potentialOption?: string): void {
        this.resetCheckoutStates(); // reset any previous error states.

        // Only for initializing the checkout, with a VIN and car ID. To then get that data, use getCheckoutData();
        try {
            if (!checkoutData && !basketOnBehalfPurchaseOrderId) throw new Error('Missing CreatePurchaseOrderRequest data');
            this.isCheckoutLoading$.next(true);

            this.initPurchaseOrderId(checkoutData || {}, basketOnBehalfPurchaseOrderId);

            this.purchaseOrderIdSubscription = this.purchaseOrderId$?.pipe(switchMap((id) => (id ? this.getPurchaseOrderById(id) : EMPTY))).subscribe(
                (res) => {
                    if (res) {
                        if (basketOnBehalfPurchaseOrderId) {
                            this.initBasketOnBehalf();
                        }

                        this.checkoutData$.next(res);
                        this.isCheckoutLoading$.next(false);

                        /**
                         * Track checkout initialization
                         * If checkout was initialised from used-car-financing, it has a preselected option
                         */
                        this.trackCheckoutStep(res, -1);
                        if (potentialOption) {
                            this.trackCheckoutStepCompletion(res, -1, potentialOption);
                        }

                        // Track step 1 impression
                        this.trackCheckoutStep(res, 0);

                        // If current step is larger than 0 (initialization step), then we have to back-track all steps
                        const currentStep = this.checkoutFlowService.currentCheckoutStepIndex;
                        if (currentStep > 0) {
                            // First track the completion of step 1
                            this.trackCheckoutStepCompletion(res, 0);

                            // Then back-track all steps
                            for (let stepIndex = 1; stepIndex <= currentStep; stepIndex++) {
                                this.trackCheckoutStep(res, stepIndex);

                                // If the step is lower than the currentStep, then it was completed
                                if (stepIndex < currentStep) {
                                    this.trackCheckoutStepCompletion(res, stepIndex);
                                }
                            }
                        }
                    }
                },
                () => {
                    this.handleFatalCheckoutError();
                }
            );
        } catch (ex: TempAny) {
            this.handleFatalCheckoutError();
            console.error('Not possible to initiate checkout: ', ex.message);
        }
    }

    initCheckoutAsBaskerOnBehalf(OfferId: string, Sid: string) {
        this.isCheckoutLoading$.next(true);
        this.isBasketOnBehalf$.next(true);

        this.http.get<ResponsesUpdateCompletedDto>(`${API_GATEWAY_PATH}/Offer/GetOffer?OfferId=${OfferId}&Sid=${Sid}`).subscribe(
            ({ purchaseOrderId }) => this.initCheckout({}, purchaseOrderId),
            () => {
                this.handleFatalCheckoutError();
            }
        );
    }

    getCheckoutData() {
        // To actually get the checkoutData(PurchaseOrder)
        return this.checkoutData$.asObservable();
    }

    resetCheckoutStates() {
        this.purchaseOrderIdSubscription?.unsubscribe();
        this.checkoutData$.next({});
        this.purchaseOrderId$.next(undefined);
        this.showGenericCheckoutError$.next(false);
        this.isBasketOnBehalf$.next(false);
    }

    setCashPayment() {
        this.concludeStep<RequestsUpdateCashRequestDto>(`${API_GATEWAY_PATH}/Checkout/SetCashPayment`, {
            cash: {
                deliveryCost: Number(this.globalSettings?.checkout.checkoutDepositAmount) || 5000,
            },
        });
    }

    setLeasingPayment(orderId: string, leasingData: any) {
        const leasingReqBody = {
            purchaseOrderId: orderId,
            lease: {
                downPayment: leasingData.downPayment,
                monthlyPayment: leasingData.monthlyPayment,
                duration: leasingData.monthlyTerms,
                leasingType: leasingData.selectedLeasingPlanType,
                taxBase: 0,
                taxPerMonth: 0,
                totalPrice: leasingData.totalPrice,
                residualValue: leasingData.residualValue,
                includeInsurance: true,
                insurancePrMonth: 0,
                kmPerYear: leasingData.yearlyDistanceInKm
            }
        };

        this.isCheckoutLoading$.next(false);
        this.concludeStep<ILeasingUsedCarUpdateRequest>(`${API_GATEWAY_PATH}/Checkout/SetLeasePayment`, leasingReqBody);
    }

    setInsuranceOnPDP(company: string) {
        const activePurchaseOrder = this.storageService.getItem(STORAGE_SERVICE_KEYS.USER_ACTIVE_PURCHASE_ORDER, true);

        if (!activePurchaseOrder) {
            return of({});
        }
        const activePurchaseOrderJSON = JSON.parse(activePurchaseOrder);

        return this.http.put(`${API_GATEWAY_PATH}/Checkout/setInsurance`, {
            purchaseOrderId: activePurchaseOrderJSON.purchaseOrderId,
            insurance: {
                company,
            },
            // createdFromOffer: false
        });
    }

    setInsurance(company: string) {
        this.concludeStep<RequestsUpdateInsuranceRequestDto>(`${API_GATEWAY_PATH}/Checkout/setInsurance`, {
            insurance: {
                company,
            },
        });
    }

    setLeasingData(leasingData: any, rangesData: any) {
        // Update isDefault on monthlyTerms range
        for (let i = 0; i < rangesData.leasingMonthsRange.length; i++) {
            rangesData.leasingMonthsRange[i].isDefault = false;

            if (leasingData.monthlyTerms === rangesData.leasingMonthsRange[i].value) {
                rangesData.leasingMonthsRange[i].isDefault = true;
            }
        }

        this.checkoutFlowLeasingData = {
            data: leasingData,
            ranges: rangesData
        };

        const leasingConfigData = {
            leasingData: leasingData,
            leasingRanges: rangesData
        };

        this.checkoutFlowLeasingData$.next(this.checkoutFlowLeasingData);
        this.broadcastUsedCarLeasingData(leasingConfigData);
    }

    broadcastUsedCarLeasingData(data: any) {
        if (!objectIsEmpty(data)) {
            this.leasingConfigDataAndRanges$.next(data);
        }
    }

    getLeasingData(): Observable<any> {
        return this.checkoutFlowLeasingData$;

        // if (this.checkoutFlowLeasingData) {
        //     // this.checkoutFlowLeasingData$.next(this.checkoutFlowLeasingData);
        // }
    }

    setOwnership({ owner, coOwner }: CheckoutUpdateOwnershipRequest, consentGiven: boolean) {
        this.isCheckoutLoading$.next(true);

        if (consentGiven) {
            this.consentService.getCheckoutConsentText().subscribe(
                ({ revisionGuid }) => {
                    this.concludeOwership({ owner, coOwner }, revisionGuid);
                },
                () => {
                    this.handleGenericCheckoutError();
                    this.isCheckoutLoading$.next(false);
                }
            );
        } else {
            this.concludeOwership({ owner, coOwner }, null);
        }
    }

    private concludeOwership({ owner, coOwner }: CheckoutUpdateOwnershipRequest, consentRevisionAcceptedGuid?: string | null) {
        this.concludeStep<CheckoutUpdateOwnershipRequest>(`${API_GATEWAY_PATH}/Checkout/SetOwnership`, {
            owner,
            coOwner,
            consentRevisionAcceptedGuid,
        });
    }

    setPickupDelivery(req: RequestsUpdatePickupDeliveryRequestDto) {
        this.concludeStep<RequestsUpdatePickupDeliveryRequestDto>(`${API_GATEWAY_PATH}/Checkout/SetPickupDelivery`, req);
    }

    setLoanPayment(loan: ISetLoanType, cb?: () => void) {
        this.concludeStep<ISetLoanRequestBody>(
            `${API_GATEWAY_PATH}/Checkout/SetLoanPayment`,
            {
                loan,
            },
            cb
        );
    }

    setExchangeCar(exchangeCarData: IExchangeCar) {
        const { valuations, make, model, trim, body, engine, valuationUrl, regDate, odometer } = exchangeCarData;
        const price = valuations?.[0]?.amount?.value;
        const brand = make?.summary;
        const brandModel = model?.summary;
        const bodyVariant = body?.summary;
        const trimVariant = trim?.summary;
        const engineVariant = engine?.summary;
        const distance = odometer?.distance;
        const year = regDate?.summary?.split('-')?.[0]; // this is not ascii art

        const reqBody: PostExchangeCarRequest = {
            exchangeCar: {
                price,
                make: brand,
                model: brandModel,
                variant: `${trimVariant}, ${bodyVariant}, ${engineVariant}`,
                indicataUrl: valuationUrl,
                year: year ? parseInt(year) : undefined,
                milage: distance,
            },
        };

        this.concludeStep<PostExchangeCarRequest>(`${API_GATEWAY_PATH}/Checkout/SetExchangeCar`, reqBody);
    }
    removeExchangeCar() {
        this.concludeStep(`${API_GATEWAY_PATH}/Checkout/SetExchangeCar`, { exchangeCar: null });
    }

    concludeStep<T>(url: string, body: T, cb?: () => void) {
        this.isCheckoutLoading$.next(true);

        this.http
            .put<ResponsesUpdateCompletedDto>(url, {
                purchaseOrderId: this.purchaseOrderId$.value,
                ...body,
            })
            .pipe(switchMap(({ purchaseOrderId }) => this.getPurchaseOrderById(purchaseOrderId)))
            .subscribe(
                (res) => {
                    this.trackCheckoutStepCompletion(res, this.checkoutFlowService.currentCheckoutStepIndex);

                    if (!this.checkoutFlowService.atFlowEnd$.value) {
                        this.purchaseOrderValidation$.next(undefined);
                    }

                    this.checkoutData$.next(res);
                    this.checkoutFlowService.nextStep();
                    this.isCheckoutLoading$.next(false);

                    this.trackCheckoutStep(res, this.checkoutFlowService.currentCheckoutStepIndex);

                    if (cb) {
                        cb();
                    }
                },
                () => {
                    this.handleGenericCheckoutError();
                }
            );
    }

    completePurchaseOrder() {
        this.isCheckoutLoading$.next(true);

        const returnUrl = `${window?.location?.origin}${this.globalSettings?.globalPages?.orderConfirmationPage?.url ?? ''}`;
        const termsUrl = `${window?.location?.origin}${this.globalSettings?.globalPages?.termsAndConditionsPage?.url ?? ''}`;
        const purchaseOrderId = this.purchaseOrderId$.value;

        if (!purchaseOrderId) {
            console.error('No purchase order id');
            return;
        }

        this.http
            .post<GoToPaymentResponse>(`${API_GATEWAY_PATH}/Checkout/CompletePurchaseOrder`, {
                purchaseOrderId,
                returnUrl,
                termsUrl,
            } as GoToPaymentRequest)
            .subscribe(
                ({ paymentPageUrl }) => {
                    this.setCompletedPurchaseOrderInStorage(purchaseOrderId);
                    this.clearPurchaseOrderInStorage();
                    if (paymentPageUrl) {
                        this.isCheckoutLoading$.next(false);
                        this.checkoutFinalized$.next(true);
                        window.location.href = paymentPageUrl;
                    }
                },
                () => {
                    this.isCheckoutLoading$.next(false);
                    this.toastService.show(this.errorMessage_couldNotGoToPayment);
                }
            );
    }

    validatePurchaseOrder() {
        this.purchaseOrderValidationLoading$.next(true);

        this.http.get<ValidatePRResponse>(`${API_GATEWAY_PATH}/Checkout/ValidatePurchaseOrder/${this.purchaseOrderId$.value}`).subscribe(
            (data) => {
                this.purchaseOrderValidation$.next(data);
                this.purchaseOrderValidationLoading$.next(false);
            },
            () => {
                this.handleGenericCheckoutError();
                this.purchaseOrderValidationLoading$.next(false);
            }
        );
    }

    setEquipment(updatedEquipmentList: IAtermarketEquipment[]) {
        this.aftermarketEquipmentLoading$.next(true);

        const reqBody: ISetAtermarketEquipmentRequest = {
            purchaseOrderId: this.purchaseOrderId$.value,
            equipment: updatedEquipmentList,
        };

        this.http
            .put<ResponsesUpdateCompletedDto>(`${API_GATEWAY_PATH}/Checkout/SetEquipment`, {
                ...reqBody,
            })
            .pipe(
                switchMap(({ purchaseOrderId }) =>
                    forkJoin({
                        aftermarketEquipmentData: this.getTotalPriceForOrder(),
                        updatedPurchaseOrder: this.getPurchaseOrderById(purchaseOrderId),
                    })
                )
            )
            .subscribe(
                ({ aftermarketEquipmentData, updatedPurchaseOrder }) => {
                    this.aftermarketEquipmentData$.next(aftermarketEquipmentData);
                    this.aftermarketEquipmentLoading$.next(false);
                    this.checkoutData$.next(updatedPurchaseOrder);

                    if (updatedPurchaseOrder.loan) {
                        this.aftermarketEquipmentLoanLoading$.next(true);
                        this.updateLoan({
                            objectPrice: aftermarketEquipmentData.carTotalPrice,
                            downPayment: updatedPurchaseOrder.loan?.downPayment,
                            paymentTerms: updatedPurchaseOrder.loan?.paymentTerms,
                            isFixedInterest: updatedPurchaseOrder.loan?.interestType === 'Fast',
                        });
                    }
                },
                () => {
                    this.aftermarketEquipmentLoading$.next(false);
                    this.handleGenericCheckoutError();
                }
            );
    }

    private getTotalPriceForOrder() {
        return this.http.get<IAtermarketEquipmentTotalOrderPriceResponse>(
            `/${API_GATEWAY_PATH}/Checkout/CalculateTotalPriceForOrder/${this.purchaseOrderId$.value}`
        );
    }

    private updateLoan(loanRequest: LoanRequest) {
        // TODO: Because BNApiGatewayApiLoanLoanResponse and BNPurchaseOrderApiLoanDtoISetLoanType have some nullable diffs, i set loanResponse to any.
        this.loanService.requestLoanData(loanRequest).subscribe((loanResponse: any) => {
            this.setLoanPayment(loanResponse, () => this.aftermarketEquipmentLoanLoading$.next(false));
        });
    }

    private initBasketOnBehalf() {
        // If this is a basket on behalf, we want to start at the summary step.
        this.checkoutFlowService.lockStep(CHECKOUT_STEP.CS_EXCHANGE_CAR);
        this.checkoutFlowService.goToStep(CHECKOUT_STEP.CS_SUMMARY);
        this.getTotalPriceForOrder().subscribe((aftermarketEquipmentData) => this.aftermarketEquipmentData$.next(aftermarketEquipmentData));
    }

    private getPurchaseOrderById(id?: string | null) {
        // private method to fetch a PurchaseOrder, if you have the ID.
        if (!id) return throwError(new Error('Cannot GetPurchaseOrder without ID'));
        return this.http.get<CheckoutGetPurchaseOrderResponse>(`${API_GATEWAY_PATH}/Checkout/GetPurchaseOrder/${id}`);
    }

    private createNewPurchaseOrder({ vin }: CheckoutCreatePurchaseOrderRequest) {
        // Create a new PurchaseOrder
        return this.http.post<ResponsesUpdateCompletedDto>(`${API_GATEWAY_PATH}/Checkout/CreatePurchaseOrder`, {
            vin,
        });
    }

    private initPurchaseOrderId(orderData: CheckoutCreatePurchaseOrderRequest, aPurchaseOrderId?: string) {
        // Used to get/calculate what PurchaseOrder ID to fetch.
        //
        // Firstly, we check the StorageService for any saved PurchaseOrder
        // IF no PurchaseOrder in found in stoarge, we'll call the API to create one.
        // In all scenarios, purchaseOrderId$ should be set.

        const activeId = aPurchaseOrderId || this.getActivePurchaseOrderIdInStorage(orderData);

        if (!activeId) {
            this.checkoutFlowService.resetFlow();
            this.createNewPurchaseOrder(orderData).subscribe(
                ({ purchaseOrderId }) => {
                    this.purchaseOrderId$?.next(purchaseOrderId);
                    this.setActivePurchaseOrderInStorage({ ...orderData, purchaseOrderId });
                },
                () => {
                    this.handleFatalCheckoutError();
                }
            );
        } else {
            this.purchaseOrderId$?.next(activeId);
        }
    }

    private getActivePurchaseOrderIdInStorage(orderData: CheckoutCreatePurchaseOrderRequest) {
        // Checks if the VIN number matches the current stored purchaseOrder, if matched, then returns ID, else null.
        const activePurchaseOrder = this.storageService.getItem(STORAGE_SERVICE_KEYS.USER_ACTIVE_PURCHASE_ORDER, true);
        if (activePurchaseOrder) {
            const { vin, purchaseOrderId }: IStoredActivePurchaseOrder = JSON.parse(activePurchaseOrder);
            return vin === orderData.vin ? purchaseOrderId : null;
        }
    }

    private setActivePurchaseOrderInStorage(orderData: IStoredActivePurchaseOrder) {
        // Lets us use StorageService to set the now active PurchaseOrder ID.
        this.storageService.setItem(STORAGE_SERVICE_KEYS.USER_ACTIVE_PURCHASE_ORDER, JSON.stringify(orderData), true);
    }

    private clearPurchaseOrderInStorage() {
        this.storageService.removeItem(STORAGE_SERVICE_KEYS.USER_ACTIVE_PURCHASE_ORDER, true);
    }

    private setCompletedPurchaseOrderInStorage(purchaseOrderId: string): void {
        this.storageService.setItem(STORAGE_SERVICE_KEYS.USER_COMPLETED_PURCHASE_ORDER_ID, purchaseOrderId, true);
    }

    private getCompletedPurchaseOrderInStorage() {
        return this.storageService.getItem(STORAGE_SERVICE_KEYS.USER_COMPLETED_PURCHASE_ORDER_ID, true);
    }

    private clearCompletedPurchaseOrderInStorage(): void {
        this.storageService.removeItem(STORAGE_SERVICE_KEYS.USER_COMPLETED_PURCHASE_ORDER_ID, true);
    }

    private handleGenericCheckoutError() {
        this.toastService.show(this.errorMessage_generic);
        this.isCheckoutLoading$.next(false);
    }

    private handleFatalCheckoutError() {
        this.showGenericCheckoutError$.next(true);
        this.checkoutFlowService.resetFlow();
        this.isCheckoutLoading$.next(false);
    }

    /**
     * Track the initialization of a checkout step.
     *
     * @param data contains the data of the checkout
     * @param stepIndex will be incremented with 2 because step 1 exists on the PDP because you choose a financing option at that time already
     */
    private trackCheckoutStep(data: CheckoutGetPurchaseOrderResponse, stepIndex: number): void {
        const step = stepIndex + 2;
        const vehicleProduct = this.convertCheckoutVehicleToEcommerce(data);

        const layer: AppGtmEcommerceOptions = {
            checkout: {
                actionField: {
                    step,
                },
                products: [vehicleProduct],
            },
        };

        this.trackingService.trackEcommerce(layer, false, 'checkout');
    }

    /**
     * Track selected checkout options depending on the index and what is defined in the data object
     *
     * @param data contains the data of the checkout
     * @param stepIndex will be incremented with 2 because step 1 exists on the PDP because you choose a financing option at that time already
     */
    private trackCheckoutStepCompletion(data: CheckoutGetPurchaseOrderResponse, stepIndex: number, option = ''): void {
        try {
            switch (stepIndex) {
                case 0:
                    option = data.exchangeCar ? 'yes' : 'no';
                    break;

                case 1:
                    if (data.loan) {
                        option = 'loan';
                    } else if (data.lease) {
                        option = 'leasing';
                    } else {
                        option = 'cash';
                    }
                    break;

                case 2:
                    if (data.insurance?.company) {
                        option = data.insurance.company;
                    }
                    break;

                case 3:
                    if (data.delivery) {
                        option = 'delivery';
                    } else {
                        option = 'pickup';
                    }
                    break;
            }

            if (!option) {
                return;
            }

            const step = stepIndex + 2;

            const options: AppGtmEcommerceOptions = {
                checkout_option: {
                    actionField: {
                        step,
                        option,
                    },
                },
            };

            this.trackingService.trackEcommerce(options, false, 'checkoutOption');
        } catch (err: any) {
            console.warn('Failed to track', err.message ?? err);
        }
    }

    async trackCheckoutCompletion(): Promise<void> {
        try {
            const purchaseOrderId = this.getCompletedPurchaseOrderInStorage();

            if (!purchaseOrderId) {
                throw new Error('No purchase order id in storage');
            }

            const data = await this.getPurchaseOrderById(purchaseOrderId).toPromise();

            if (!data) {
                throw new Error('Unable to find purchase order from id');
            }

            const { brandName, product, consentRevisionAcceptedGuid } = data;

            if (!product) {
                throw new Error('No product in purchase');
            }

            const vehicleProduct = this.convertCheckoutVehicleToEcommerce(data);

            const options: AppGtmEcommerceOptions = {
                purchase: {
                    actionField: {
                        affiliation: brandName || '',
                        id: purchaseOrderId,
                        revenue: vehicleProduct.price,
                        shipping: '0.0',
                        tax: product?.taxAmount?.toFixed(1) || '0.0',
                    },
                    products: [vehicleProduct],
                },
            };

            this.trackingService.trackEcommerce(options);

            if (consentRevisionAcceptedGuid) {
                this.trackingService.trackConsentGiven();
            }

            this.clearCompletedPurchaseOrderInStorage();
        } catch (err: any) {
            console.warn('Failed track purchase completion:', err.message ?? err);
        }
    }

    private convertCheckoutVehicleToEcommerce(data: CheckoutGetPurchaseOrderResponse): EcommerceProduct {
        const { product } = data;

        if (!product) {
            throw new Error('Checkout contained no product');
        }

        return mapProductToEcommerceVehicle(product);
    }
}
